/**
 * Thin, shared wrapper around `@simplewebauthn/browser` — every app that has
 * a login form (platform-admin, dashboard-admin, ecommerce-store) uses the
 * exact same registration/authentication flow, so it lives here once
 * instead of three copies. `@simplewebauthn/browser`'s `startRegistration`/
 * `startAuthentication` already produce/consume the exact JSON shapes this
 * backend's `webauthn` (py_webauthn) endpoints emit/expect
 * (`PublicKeyCredentialCreationOptionsJSON`/`...RequestOptionsJSON` and
 * `RegistrationResponseJSON`/`AuthenticationResponseJSON` — the same
 * official WebAuthn JSON schema both libraries implement), so there is no
 * base64url/ArrayBuffer conversion glue needed here at all.
 */
import { browserSupportsWebAuthn, startAuthentication, startRegistration, WebAuthnError } from "@simplewebauthn/browser";
/** True in any browser with platform WebAuthn support (Face ID, fingerprint, Windows
 * Hello, a security key) — every login/account-settings page should hide its
 * "Use a passkey instead" affordance when this is false rather than showing a button
 * that can only ever fail. */
export function isPasskeySupported() {
    return browserSupportsWebAuthn();
}
export class PasskeyCancelledError extends Error {
    constructor() {
        super("No passkey was available, or the request was cancelled or timed out.");
        this.name = "PasskeyCancelledError";
    }
}
/** Both codes below are the browser declining to produce a credential, just for different
 * reasons: `ERROR_CEREMONY_ABORTED` is a programmatic abort (component unmount, a second
 * ceremony superseding this one). `ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY` is simplewebauthn's
 * wrapper around a raw `NotAllowedError` DOMException, which is what browsers actually throw
 * for "user dismissed the prompt", "the request timed out", AND "no passkey matches this site"
 * — per the WebAuthn spec's privacy considerations
 * (https://www.w3.org/TR/webauthn-2/#sctn-privacy-considerations-client), browsers deliberately
 * collapse all three into one indistinguishable error so a site can't probe whether a given
 * user has a credential registered. That raw message is technically accurate but meaningless
 * to an end user, so every one of these gets normalized to `PasskeyCancelledError` instead of
 * leaking browser/spec text into the UI. */
function rethrowCancellation(error) {
    if (error instanceof WebAuthnError &&
        (error.code === "ERROR_CEREMONY_ABORTED" || error.code === "ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY")) {
        throw new PasskeyCancelledError();
    }
    throw error;
}
/** Drives `navigator.credentials.create()` — call with the `options` object returned
 * by `POST .../webauthn/register/begin`. Throws `PasskeyCancelledError` if the user
 * dismisses the platform prompt (a normal, expected outcome — not a real error), so
 * callers can show "Cancelled" instead of a scary error banner for that one case. */
export async function registerPasskey(optionsJSON) {
    try {
        return await startRegistration({ optionsJSON: optionsJSON });
    }
    catch (error) {
        rethrowCancellation(error);
    }
}
/** Drives `navigator.credentials.get()` — call with the `options` object returned by
 * `POST .../webauthn/login/begin`. Usernameless/discoverable by design (the backend's
 * `begin_authentication` never sends `allowCredentials`), so the browser prompts with
 * whichever passkeys it has for this site — no email typed anywhere. */
export async function authenticateWithPasskey(optionsJSON) {
    try {
        return await startAuthentication({ optionsJSON: optionsJSON });
    }
    catch (error) {
        rethrowCancellation(error);
    }
}
//# sourceMappingURL=index.js.map